<script setup lang="ts">
import { ZhPageTitle } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api, type Member } from '@/api'
import MailboxPanel from '@/components/MailboxPanel.vue'
import { memberMailboxSource } from '@/lib/mailboxSources'

/**
 * One mail of a member's mailbox on the whole page (`/members/:id/mail/:emailId`): its conversation, its ids and, for
 * the enterprise owner, the commands the member's page offers on it. Read under the `members` grant, like the page.
 */
const route = useRoute()
const memberId = String(route.params['id'])
const emailId = computed(() => String(route.params['emailId']))
const member = ref<Member | null>(null)
const isOwner = ref<boolean | null>(null)

onMounted(async () => {
    const [found, me] = await Promise.allSettled([api.member(memberId), api.me()])
    member.value = found.status === 'fulfilled' ? found.value : null
    isOwner.value = me.status === 'fulfilled' && me.value?.isOwner === true
})

const source = computed(() => memberMailboxSource(memberId, isOwner.value === true))
</script>

<template>
	<section class="w-full space-y-4 p-8" data-testid="member-mail-view">
		<ZhPageTitle kicker="Mail">
			{{ member?.name ?? 'Mailbox' }}
			<template v-if="member?.email" #meta>{{ member.email }}</template>
		</ZhPageTitle>
		<p v-if="isOwner === null" class="text-muted-foreground text-sm">Loading…</p>
		<MailboxPanel v-else :source="source" :email-id="emailId" />
	</section>
</template>
